import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  VStack,
  Image,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react'
import { FileIcon, X, ZoomIn } from 'lucide-react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/store/auth'
import { ProfileService } from '@/lib/services/profileService'
import { isRequestorRole } from '@/lib/utils/roleUtils'
import ReactSelect from 'react-select'
import type { User } from '@/types'

function getAuthUserEmail(user: User | null | undefined): string {
  if (!user) return ''
  const record = user as User & { user_email?: string; user_details?: Record<string, unknown> }
  return (
    record.user_email?.trim() ||
    user.email?.trim() ||
    (typeof record.user_details?.user_email === 'string' ? record.user_details.user_email.trim() : '') ||
    ''
  )
}

export type TicketRequestFormProps = {
  ticketRequestId?: number
  onSuccess?: () => void
  onCancel?: () => void
  /** API path for ticket request CRUD (e.g. 'ticket-management/all-tickets' or 'support/my-request') */
  basePath?: string
  /** When true, hide Severity, Related to, and Assign to (for requestor role) */
  requestorMode?: boolean
  /** When this value changes, the form refetches ticket data (e.g. after reassign). */
  refreshKey?: number
}

const DEFAULT_BASE_PATH = 'ticket-management/all-tickets'

type ServiceTypeOption = { id: number; name: string; code: string; parent_id: number | null; approval?: boolean }

type SavedAttachmentMeta = { name: string; size?: number; type?: string; file_url?: string | null; thumbnail_url?: string | null; media_id?: number | null }
type AttachmentPreview = {
  file: File
  preview?: string
  /** True when loaded from server (no real File); use savedMeta for payload */
  isExisting?: boolean
  savedMeta?: SavedAttachmentMeta
}

type TicketSearchItem = { id: number; request_number: string }

type UserSearchItem = {
  id: number
  user_login: string
  user_email?: string
  first_name?: string | null
  last_name?: string | null
}

type RequesterSearchItem = {
  id: number
  user_login: string
  user_email?: string
  first_name?: string | null
  last_name?: string | null
  user_details?: Record<string, unknown>
  department_name?: string | null
}

/** react-select option shape for requester (same UX pattern as Service type) */
type RequesterSelectOption = { value: number; label: string; user: RequesterSearchItem }

function userLabelFromRequester(
  u: Pick<RequesterSearchItem, 'user_login' | 'user_email' | 'first_name' | 'last_name'> | null
) {
  if (!u) return ''
  const first = u.first_name ?? ''
  const last = u.last_name ?? ''
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name || u.user_login || u.user_email || ''
}

function getRequesterContactDetails(user: RequesterSearchItem | Record<string, unknown>) {
  const requester = user as RequesterSearchItem
  return {
    contact_name: userLabelFromRequester(requester),
    contact_email: requester.user_email?.trim() || '',
    department: requester.department_name?.trim() || '',
  }
}

const initialValues = {
  request_number: '',
  service_type_id: '' as string | number,
  category_id: '' as string | number,
  sub_category_id: '' as string | number,
  item_id: '' as string | number,
  description: '',
  /** Physical / site location for the ticket (API: location) */
  location: '',
  ticket_priority_id: '' as string | number,
  contact_number: '',
  contact_name: '',
  contact_email: '',
  department: '',
  slas_id: '' as string | number,
}

const RELATIONSHIP_TYPES = [
  { value: 'duplicate_of', label: 'Duplicate of' },
  { value: 'parent_of', label: 'Parent of' },
  { value: 'child_of', label: 'Child of' },
  { value: 'relates_to', label: 'Relates to' },
] as const

export function TicketRequestForm({ ticketRequestId, onSuccess, onCancel, basePath = DEFAULT_BASE_PATH, requestorMode = false, refreshKey }: TicketRequestFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!ticketRequestId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [allServiceTypes, setAllServiceTypes] = useState<ServiceTypeOption[]>([])
  const [slas, setSlas] = useState<{ id: number; severity: string }[]>([])
  const [ticketPriorities, setTicketPriorities] = useState<{ id: number; label: string; level: number }[]>([])
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const [zoomAttachment, setZoomAttachment] = useState<AttachmentPreview | null>(null)
  const [parentTicketId, setParentTicketId] = useState<number | null>(null)
  const [relatedToDisplay, setRelatedToDisplay] = useState<string>('')
  const [relationshipType, setRelationshipType] = useState<string>('relates_to')
  const [relatedSearchQuery, setRelatedSearchQuery] = useState('')
  const [relatedSearchResults, setRelatedSearchResults] = useState<TicketSearchItem[]>([])
  const [relatedSearching, setRelatedSearching] = useState(false)
  const relatedSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [assignedToId, setAssignedToId] = useState<number | null>(null)
  const [assignToDisplay, setAssignToDisplay] = useState<string>('')
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<UserSearchItem[]>([])
  const [userSearching, setUserSearching] = useState(false)
  const userSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [assignToMe, setAssignToMe] = useState(false)
  const [selectedRequester, setSelectedRequester] = useState<RequesterSearchItem | null>(null)
  const [requesterDepartment, setRequesterDepartment] = useState<string>('')
  const [requesterSearchQuery, setRequesterSearchQuery] = useState('')
  const [requesterSearchResults, setRequesterSearchResults] = useState<RequesterSearchItem[]>([])
  const [requesterSearching, setRequesterSearching] = useState(false)
  const requesterSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentUser = useAuthStore((s) => s.user)
  const isLoggedInRequestor = isRequestorRole(currentUser)
  const isRequestorCreateAutofill = isLoggedInRequestor && !ticketRequestId
  // Non-Requestor create: autofill/read-only contact from selected requester (incl. Web Admin).
  // Same gate as Requester-after-Attachment so role-name resolution misses do not skip Web Admin.
  const isStaffRequesterAutofillCreate = !isRequestorCreateAutofill && !requestorMode && !ticketRequestId
  const isRequesterAfterAttachment = isStaffRequesterAutofillCreate
  const showRequesterSelect = isStaffRequesterAutofillCreate
  const isStaffRequesterAutofill = isStaffRequesterAutofillCreate && !!selectedRequester
  const isContactFieldsReadOnly = isRequestorCreateAutofill || isStaffRequesterAutofillCreate
  const { isOpen: isZoomOpen, onOpen: onZoomOpen, onClose: onZoomClose } = useDisclosure()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Pre-calculate parent Category, SubCategory and Item children mapping for easier rendering and lookup
  type Category = { id: number; name: string }
  type SubCategory = { id: number; name: string }
  type Item = { id: number; name: string; subcategory_id: number[] }

  const [parentCategories, setParentCategories] = useState<Category[]>([])
  const [subCategories, setSubCategories] = useState<SubCategory[]>([])
  const [items, setItems] = useState<Item[]>([])

  const [selectedParent, setSelectedParent] = useState<number | ''>('')
  const [selectedSubCategory, setSelectedSubCategory] = useState<number | ''>('')
  const [selectedItem, setSelectedItem] = useState<number | ''>('')

  const loadParentCategories = useCallback(async () => {
    try {
      const { data } = await apiClient.get(
        '/category-management/categories/dropdown'
      )
      const list = data?.data ?? data ?? []
      setParentCategories(list)
    } catch (e) {
      console.error('Failed to load parent categories', e)
    }
  }, [])

  useEffect(() => {
    if (!selectedParent) {
      setSubCategories([])
      setSelectedSubCategory('')
      setItems([])
      setSelectedItem('')
      return
    }

    const fetchSub = async () => {
      try {
        const { data } = await apiClient.get(
          `/category-management/categories/dropdown/${selectedParent}`
        )
        const nextSubCategories = data?.data ?? data ?? []
        setSubCategories(nextSubCategories)

        if (
          selectedSubCategory &&
          !nextSubCategories.some((sub: SubCategory) => sub.id === Number(selectedSubCategory))
        ) {
          setSelectedSubCategory('')
          setSelectedItem('')
          setForm((prev) => ({ ...prev, sub_category_id: '', item_id: '' }))
        }
      } catch (e) {
        console.error('Failed to load subcategories', e)
      }
    }

    fetchSub()
  }, [selectedParent])

  useEffect(() => {
    if (!selectedSubCategory) {
      setItems([])
      setSelectedItem('')
      return
    }

  const fetchItems = async () => {
      try {
        const { data } = await apiClient.get(
          '/category-management/items',
          { params: { per_page: 1000 } }
        )

        const payload = data?.data ?? data ?? []
        const list: Item[] = Array.isArray(payload) ? payload : payload?.data ?? []
        const selectedSubCategoryId = Number(selectedSubCategory)

        // FILTER: only items containing subcategory_id in array
        const filtered = list.filter((item) =>
          Array.isArray(item.subcategory_id) &&
          item.subcategory_id.map((id) => Number(id)).includes(selectedSubCategoryId)
        )

        setItems(filtered)

        if (
          selectedItem &&
          !filtered.some((item) => item.id === Number(selectedItem))
        ) {
          setSelectedItem('')
          setForm((prev) => ({ ...prev, item_id: '' }))
        }
      } catch (e) {
        console.error('Failed to load items', e)
      }
    }

    fetchItems()
  }, [selectedSubCategory])

  useEffect(() => {
    loadParentCategories()
  }, [loadParentCategories])

// --------------------------------------------------------

  const serviceTypeParents = useMemo(
    () => allServiceTypes.filter((st) => st.parent_id == null),
    [allServiceTypes]
  )

  const getChildren = useCallback(
    (parentId: number | null) => {
      if (parentId == null) return []
      return allServiceTypes.filter((st) => st.parent_id === parentId)
    },
    [allServiceTypes]
  )

  const categoryOptions = useMemo(() => {
    return form.service_type_id ? getChildren(Number(form.service_type_id)) : []
  }, [form.service_type_id, getChildren])

  const subCategoryOptions = useMemo(() => {
    return form.category_id ? getChildren(Number(form.category_id)) : []
  }, [form.category_id, getChildren])

  const itemOptions = useMemo(() => {
    return form.sub_category_id ? getChildren(Number(form.sub_category_id)) : []
  }, [form.sub_category_id, getChildren])

  const loadOptions = useCallback(async () => {
    try {
      const [typesRes, slaRes, ticketPriorityRes] = await Promise.all([
        apiClient.get('/service-catalog/service-types', { params: { per_page: 200 } }),
        apiClient.get('/service-catalog/sla-timing', { params: { per_page: 200 } }),
        apiClient.get('/service-catalog/ticket-priorities', { params: { per_page: 200 } }),
      ])
      const typesData = typesRes.data?.data ?? typesRes.data
      const slaData = slaRes.data?.data ?? slaRes.data
      const ticketPriorityData = ticketPriorityRes.data?.data ?? ticketPriorityRes.data
      const list = Array.isArray(typesData) ? typesData : typesData?.data ?? []
      setAllServiceTypes(list)
      setSlas(Array.isArray(slaData) ? slaData : [])
      setTicketPriorities(Array.isArray(ticketPriorityData) ? ticketPriorityData : [])
    } catch (e) {
      console.error('Failed to load options', e)
    }
  }, [])

  const loadOne = useCallback(async () => {
  if (!ticketRequestId) return
  setLoading(true)
  setErrors({})

  try {
    const { data } = await apiClient.get(`${basePath}/${ticketRequestId}`)
    const r = data?.data ?? data

    const selectedId = r.service_type_id ?? ''

    let serviceTypeId: number | '' = ''
    let categoryId: number | '' = ''
    let subCategoryId: number | '' = ''
    let itemId: number | '' = ''

    if (selectedId) {
      let current = allServiceTypes.find((s) => s.id === selectedId)

      // Walk up the tree
      const chain: ServiceTypeOption[] = []
      while (current) {
        chain.push(current)
        if (current.parent_id == null) break
        current = allServiceTypes.find((s) => s.id === current!.parent_id)
      }

      // chain[0] = selected
      // chain[1] = parent
      // chain[2] = grandparent ...
      // So we reverse for easier mapping (top-down)
      const reversed = [...chain].reverse()

      if (reversed.length === 1) {
        // Service type only
        serviceTypeId = reversed[0].id
      }

      if (reversed.length === 2) {
        // service type → category
        serviceTypeId = reversed[0].id
        categoryId = reversed[1].id
      }

      if (reversed.length === 3) {
        // service type → category → subcategory
        serviceTypeId = reversed[0].id
        categoryId = reversed[1].id
        subCategoryId = reversed[2].id
      }

      if (reversed.length >= 4) {
        // service type → category → subcategory → item
        serviceTypeId = reversed[0].id
        categoryId = reversed[1].id
        subCategoryId = reversed[2].id
        itemId = reversed[3].id
      }
    }

    const parentCategoryId = r.category_id ?? ''
    const subCategoryIdFromApi = r.subcategory_id ?? r.sub_category_id ?? ''
    const itemIdFromApi = r.item_id ?? ''

    setForm({
      request_number: r.request_number ?? '',
      service_type_id: serviceTypeId,
      category_id: parentCategoryId || categoryId,
      sub_category_id: subCategoryIdFromApi || subCategoryId,
      item_id: itemIdFromApi || itemId,
      description: r.description ?? '',
      location: typeof r.location === 'string' ? r.location : r.location != null ? String(r.location) : '',
      ticket_priority_id: r.ticket_priority_id ?? '',
      contact_number: r.contact_number ?? '',
      contact_name: r.contact_name ?? '',
      contact_email: r.contact_email ?? '',
      department: typeof r.department === 'string' ? r.department : r.department != null ? String(r.department) : '',
      slas_id: r.slas_id ?? '',
    })
    setRequesterDepartment(typeof r.department === 'string' ? r.department : r.department != null ? String(r.department) : '')
    setSelectedParent(parentCategoryId || '')
      setSelectedSubCategory(subCategoryIdFromApi || '')
      setSelectedItem(itemIdFromApi || '')
      const parentId = r.parent_ticket_id ?? null
      setParentTicketId(parentId)
      if (parentId && parentId !== ticketRequestId) {
        try {
          const parentRes = await apiClient.get(`${basePath}/${parentId}`)
          const parentData = parentRes.data?.data ?? parentRes.data
          setRelatedToDisplay(parentData?.request_number ?? '')
        } catch {
          setRelatedToDisplay('')
        }
      } else {
        setRelatedToDisplay('')
      }
      if (r.assigned_to) {
        setAssignedToId(r.assigned_to)
        try {
          const userRes = await apiClient.get(`/user-management/users/${r.assigned_to}`)
          const u = userRes.data?.data ?? userRes.data
          setAssignToDisplay(userLabel(u) ?? '')
        } catch {
          setAssignToDisplay('')
        }
        setAssignToMe(r.assigned_to === currentUser?.id)
      } else {
        setAssignedToId(null)
        setAssignToDisplay('')
        setAssignToMe(false)
      }
      const meta = r.attachment_metadata
      if (Array.isArray(meta) && meta.length > 0) {
        setAttachments(
          meta.map((m: SavedAttachmentMeta & { file_name?: string }) => ({
            file: new File([], m?.name ?? m?.file_name ?? 'file'),
            preview: undefined,
            isExisting: true,
            savedMeta: {
              name: m?.name ?? m?.file_name ?? 'file',
              size: m?.size,
              type: m?.type,
              file_url: m?.file_url ?? null,
              thumbnail_url: m?.thumbnail_url ?? null,
              media_id: m?.media_id ?? null,
            },
          }))
        )
      } else {
        setAttachments([])
      }
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [ticketRequestId, showError, allServiceTypes, currentUser?.id])

  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  // Reset only when entering create mode — not when async dropdown options finish loading.
  useEffect(() => {
    if (ticketRequestId) return
    setForm(initialValues)
    setSelectedParent('')
    setSelectedSubCategory('')
    setSelectedItem('')
    setSubCategories([])
    setItems([])
    setAttachments([])
    setParentTicketId(null)
    setRelatedToDisplay('')
    setRelatedSearchResults([])
    setAssignedToId(null)
    setAssignToDisplay('')
    setAssignToMe(false)
    setUserSearchResults([])
    setSelectedRequester(null)
    setRequesterSearchQuery('')
    setRequesterSearchResults([])
  }, [ticketRequestId, refreshKey])

  useEffect(() => {
    if (!ticketRequestId) return
    if (allServiceTypes.length === 0) return
    loadOne()
  }, [ticketRequestId, allServiceTypes.length, loadOne, refreshKey])

  const searchRelatedTickets = useCallback(async (query: string) => {
    if (!query.trim()) {
      setRelatedSearchResults([])
      return
    }
    setRelatedSearching(true)
    try {
      const { data } = await apiClient.get(basePath, { params: { search: query.trim(), per_page: 10 } })
      const list = data?.data ?? data
      const items = Array.isArray(list) ? list : list?.data ?? []
      const filtered = items.filter((t: TicketSearchItem) => t.id !== ticketRequestId)
      setRelatedSearchResults(filtered)
    } catch {
      setRelatedSearchResults([])
    } finally {
      setRelatedSearching(false)
    }
  }, [ticketRequestId])

  useEffect(() => {
    if (relatedSearchTimeoutRef.current) clearTimeout(relatedSearchTimeoutRef.current)
    if (!relatedSearchQuery.trim()) {
      setRelatedSearchResults([])
      return
    }
    relatedSearchTimeoutRef.current = setTimeout(() => {
      searchRelatedTickets(relatedSearchQuery)
    }, 300)
    return () => {
      if (relatedSearchTimeoutRef.current) clearTimeout(relatedSearchTimeoutRef.current)
    }
  }, [relatedSearchQuery, searchRelatedTickets])

  const selectRelatedTicket = (ticket: TicketSearchItem) => {
    setParentTicketId(ticket.id)
    setRelatedToDisplay(ticket.request_number)
    setRelatedSearchQuery('')
    setRelatedSearchResults([])
  }

  const clearRelatedTicket = () => {
    setParentTicketId(null)
    setRelatedToDisplay('')
    setRelatedSearchQuery('')
    setRelatedSearchResults([])
  }

  const userLabel = (u: UserSearchItem | { user_login?: string; user_email?: string; first_name?: string | null; last_name?: string | null } | null) => {
    if (!u) return ''
    const first = (u as UserSearchItem).first_name ?? ''
    const last = (u as UserSearchItem).last_name ?? ''
    const name = [first, last].filter(Boolean).join(' ').trim()
    return name || (u as UserSearchItem).user_login || (u as any).user_email || ''
  }

  const searchUsers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setUserSearchResults([])
      return
    }
    setUserSearching(true)
    try {
      const { data } = await apiClient.get('/user-management/users', { params: { search: query.trim(), per_page: 10 } })
      const list = data?.data ?? data
      const items = Array.isArray(list) ? list : list?.data ?? []
      setUserSearchResults(items)
    } catch {
      setUserSearchResults([])
    } finally {
      setUserSearching(false)
    }
  }, [])

  useEffect(() => {
    if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current)
    if (!userSearchQuery.trim() || userSearchQuery.trim().length < 2) {
      setUserSearchResults([])
      return
    }
    userSearchTimeoutRef.current = setTimeout(() => {
      searchUsers(userSearchQuery)
    }, 300)
    return () => {
      if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current)
    }
  }, [userSearchQuery, searchUsers])

  const searchRequesters = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setRequesterSearchResults([])
      return
    }
    setRequesterSearching(true)
    try {
      const { data } = await apiClient.get('/user-management/users', { params: { search: query.trim(), per_page: 10 } })
      const list = data?.data ?? data
      const items = Array.isArray(list) ? list : list?.data ?? []
      setRequesterSearchResults(items)
    } catch {
      setRequesterSearchResults([])
    } finally {
      setRequesterSearching(false)
    }
  }, [])

  useEffect(() => {
    if (requesterSearchTimeoutRef.current) clearTimeout(requesterSearchTimeoutRef.current)
    if (!requesterSearchQuery.trim() || requesterSearchQuery.trim().length < 2) {
      setRequesterSearchResults([])
      return
    }
    requesterSearchTimeoutRef.current = setTimeout(() => {
      searchRequesters(requesterSearchQuery)
    }, 300)
    return () => {
      if (requesterSearchTimeoutRef.current) clearTimeout(requesterSearchTimeoutRef.current)
    }
  }, [requesterSearchQuery, searchRequesters])

  const currentUserDepartment = useMemo(() => {
    return (
      (currentUser as any)?.department_name ??
      (currentUser as any)?.department ??
      (currentUser as any)?.user_details?.department_name ??
      ''
    )
  }, [currentUser])

  const currentUserContactName = useMemo(() => {
    if (!currentUser) return ''
    const first = (currentUser as any).first_name ?? ''
    const last = (currentUser as any).last_name ?? ''
    const name = [first, last].filter(Boolean).join(' ').trim()
    return name || currentUser.name || currentUser.user_login || ''
  }, [currentUser])

  const currentUserContactEmail = useMemo(() => getAuthUserEmail(currentUser), [currentUser])

  const applyContactFields = useCallback((contactName: string, contactEmail: string) => {
    setForm((prev) => ({
      ...prev,
      contact_name: contactName,
      contact_email: contactEmail,
    }))
    setErrors((prev) => {
      const next = { ...prev }
      let changed = false
      if (contactName.trim() && next.contact_name) {
        delete next.contact_name
        changed = true
      }
      if (contactEmail.trim() && next.contact_email) {
        delete next.contact_email
        changed = true
      }
      return changed ? next : prev
    })
  }, [])

  const selectRequesterUser = useCallback(
    async (user: RequesterSearchItem) => {
      setSelectedRequester(user)
      setRequesterSearchQuery('')
      setRequesterSearchResults([])
      setErrors((prev) => {
        if (!prev.requester) return prev
        const next = { ...prev }
        delete next.requester
        return next
      })

      const immediate = getRequesterContactDetails(user)
      setRequesterDepartment(immediate.department)

      if (isStaffRequesterAutofillCreate) {
        applyContactFields(immediate.contact_name, immediate.contact_email)
      }

      try {
        const { data } = await apiClient.get(`/user-management/users/${user.id}`)
        const profile = { ...user, ...((data?.data ?? data) as RequesterSearchItem) }
        const enriched = getRequesterContactDetails(profile)
        setRequesterDepartment(enriched.department)
        if (isStaffRequesterAutofillCreate) {
          applyContactFields(enriched.contact_name, enriched.contact_email)
        }
      } catch {
        // Non-blocking: keep search-result values when profile lookup fails.
      }
    },
    [applyContactFields, isStaffRequesterAutofillCreate]
  )

  const clearRequesterUser = useCallback(() => {
    setSelectedRequester(null)
    setRequesterSearchQuery('')
    setRequesterSearchResults([])

    if (isRequestorCreateAutofill || requestorMode) {
      setRequesterDepartment(currentUserDepartment)
    } else {
      setRequesterDepartment('')
    }

    if (isStaffRequesterAutofillCreate) {
      setForm((prev) => ({
        ...prev,
        contact_name: '',
        contact_email: '',
      }))
    }
  }, [isStaffRequesterAutofillCreate, isRequestorCreateAutofill, requestorMode, currentUserDepartment])

  useEffect(() => {
    if ((!requestorMode && !isRequestorCreateAutofill) || ticketRequestId) return
    setRequesterDepartment(currentUserDepartment)
  }, [requestorMode, isRequestorCreateAutofill, ticketRequestId, currentUserDepartment])

  useEffect(() => {
    if (!isRequestorCreateAutofill) return

    const applyRequestorAutofill = (contactName: string, contactEmail: string, department?: string) => {
      applyContactFields(contactName, contactEmail)
      if (department) setRequesterDepartment(department)
    }

    if (currentUserContactEmail) {
      applyRequestorAutofill(currentUserContactName, currentUserContactEmail, currentUserDepartment || undefined)
      return
    }

    let cancelled = false
    ProfileService.getMe()
      .then((res) => {
        if (cancelled) return
        const data = (res.data ?? res) as Record<string, unknown>
        const contactEmail =
          (typeof data.user_email === 'string' ? data.user_email : '') ||
          (typeof data.email === 'string' ? data.email : '')
        const contactName =
          [data.first_name, data.last_name].filter((part) => typeof part === 'string' && part.trim()).join(' ').trim() ||
          (typeof data.user_login === 'string' ? data.user_login : '') ||
          currentUserContactName
        const department =
          (typeof data.department_name === 'string' ? data.department_name : '') ||
          currentUserDepartment
        if (contactEmail) {
          applyRequestorAutofill(contactName, contactEmail, department || undefined)
        }
      })
      .catch(() => {
        // Non-blocking: auth store may still populate email after initializeAuth completes.
      })

    return () => {
      cancelled = true
    }
  }, [
    applyContactFields,
    isRequestorCreateAutofill,
    currentUserContactName,
    currentUserContactEmail,
    currentUserDepartment,
  ])

  const selectAssignUser = (user: UserSearchItem) => {
    setAssignedToId(user.id)
    setAssignToDisplay(userLabel(user))
    setUserSearchQuery('')
    setUserSearchResults([])
    setAssignToMe(false)
  }

  const clearAssignTo = () => {
    setAssignedToId(null)
    setAssignToDisplay('')
    setUserSearchQuery('')
    setUserSearchResults([])
    setAssignToMe(false)
  }

  const handleAssignToMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setAssignToMe(checked)
    if (checked && currentUser) {
      setAssignedToId(currentUser.id)
      setAssignToDisplay(userLabel({ id: currentUser.id, user_login: currentUser.user_login, user_email: currentUser.email, first_name: (currentUser as any).first_name, last_name: (currentUser as any).last_name }))
      setUserSearchQuery('')
      setUserSearchResults([])
    } else if (!checked) {
      setAssignedToId(null)
      setAssignToDisplay('')
    }
  }

  const updateField = (field: keyof typeof form, value: string | number) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value }

      if (field === 'service_type_id') {
        next.category_id = ''
        next.sub_category_id = ''
        next.item_id = ''
      }

      if (field === 'category_id') {
        next.sub_category_id = ''
        next.item_id = ''
      }

      if (field === 'sub_category_id') {
        next.item_id = ''
      }

      return next
    })

    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const effectiveServiceTypeId = useMemo(() => {
    // Always submit the real service type ID.
    // Category/subcategory/item are separate taxonomies and must not override service_type_id.
    if (form.service_type_id !== '' && form.service_type_id != null) return Number(form.service_type_id)
    if (form.item_id && allServiceTypes.some((s) => s.id === Number(form.item_id))) return Number(form.item_id)
    if (form.sub_category_id && allServiceTypes.some((s) => s.id === Number(form.sub_category_id))) return Number(form.sub_category_id)
    if (form.category_id && allServiceTypes.some((s) => s.id === Number(form.category_id))) return Number(form.category_id)
    return null
  }, [form, allServiceTypes])

  const effectiveServiceType = useMemo(() => {
    if (effectiveServiceTypeId == null) return null
    return allServiceTypes.find((s) => s.id === effectiveServiceTypeId) ?? null
  }, [effectiveServiceTypeId, allServiceTypes])

  const isImage = (f: File) => f.type.startsWith('image/')

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files?.length) return
    const next: AttachmentPreview[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const preview = isImage(file) ? URL.createObjectURL(file) : undefined
      next.push({ file, preview })
    }
    setAttachments((prev) => [...prev, ...next])
    e.target.value = ''
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const item = prev[index]
      if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview)
      return prev.filter((_, i) => i !== index)
    })
  }

  const openZoom = (item: AttachmentPreview) => {
    setZoomAttachment(item)
    onZoomOpen()
  }

  const closeZoom = () => {
    setZoomAttachment(null)
    onZoomClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    // Required fields validation
    const newErrors: Record<string, string[]> = {}
    if (effectiveServiceTypeId == null) {
      newErrors.service_type_id = ['Service type is required.']
    }
    if (!requestorMode && (form.slas_id === '' || form.slas_id == null)) {
      newErrors.slas_id = ['Severity is required.']
    }
    if (!requestorMode && (form.ticket_priority_id === '' || form.ticket_priority_id == null)) {
      newErrors.ticket_priority_id = ['Priority is required.']
    }
    if (!form.description?.trim()) {
      newErrors.description = ['Description is required.']
    }
    if (!ticketRequestId && !form.location?.trim()) {
      newErrors.location = ['Location is required.']
    }
    if (showRequesterSelect && !selectedRequester) {
      newErrors.requester = ['Requester is required.']
    }
    if (Object.keys(newErrors).length > 0) {
      console.log('Validation errors:', newErrors)
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const existingMeta = attachments
        .filter((a) => a.isExisting && a.savedMeta)
        .map((a) => ({
          name: a.savedMeta!.name,
          size: a.savedMeta!.size ?? 0,
          type: a.savedMeta!.type ?? '',
          file_url: a.savedMeta!.file_url ?? null,
          thumbnail_url: a.savedMeta!.thumbnail_url ?? null,
          media_id: a.savedMeta!.media_id ?? null,
        }))
      const newFiles = attachments.filter((a) => !a.isExisting)
      let attachment_metadata: Array<{ name: string; size?: number; type?: string; file_url?: string | null; thumbnail_url?: string | null; media_id?: number | null }> = [...existingMeta]

      if (newFiles.length > 0) {
        const fd = new FormData()
        newFiles.forEach((a) => fd.append('attachments[]', a.file))
        const uploadRes = await apiClient.post(`${basePath}/upload-attachments`, fd, { headers: { 'Content-Type': undefined } })
        const uploaded = (uploadRes.data?.attachment_metadata ?? uploadRes.data ?? []) as typeof attachment_metadata
        if (Array.isArray(uploaded) && uploaded.length > 0) {
          const normalized = uploaded.map((u) => ({
            name: u.name,
            size: u.size ?? 0,
            type: u.type ?? '',
            file_url: u.file_url ?? null,
            thumbnail_url: u.thumbnail_url ?? null,
            media_id: u.media_id ?? null,
          }))
          attachment_metadata = existingMeta.concat(normalized)
        }
      }

      const payload: Record<string, unknown> = {
        parent_ticket_id: requestorMode ? null : parentTicketId,
        service_type_id: effectiveServiceTypeId,
        category_id: selectedParent || null,
        subcategory_id: selectedSubCategory || null,
        item_id: selectedItem || null,
        ticket_priority_id: requestorMode ? null : Number(form.ticket_priority_id),
        description: form.description.trim() || null,
        contact_number: form.contact_number.trim() || null,
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        department: requesterDepartment?.trim() || form.department?.trim() || null,
        location: form.location.trim() || null,
        slas_id: requestorMode ? null : (form.slas_id === '' ? null : Number(form.slas_id)),
        for_approval: effectiveServiceType?.approval ? 1 : 2,
        assigned_to: requestorMode ? null : (assignToMe ? (currentUser?.id ?? null) : assignedToId),
        attachment_metadata: attachment_metadata.length > 0 ? attachment_metadata : null,
      }
      // Link ticket to selected requester (sets user_id server-side for CSAT routing to requester email).
      if (!requestorMode && !ticketRequestId && selectedRequester?.id != null) {
        payload.requester_user_id = selectedRequester.id
      }

      if (ticketRequestId) {
        await apiClient.put(`${basePath}/${ticketRequestId}`, payload)
        if (!requestorMode && parentTicketId) {
          await apiClient.post(`${basePath}/${ticketRequestId}/relationships`, {
            target_ticket_id: parentTicketId,
            relationship_type: relationshipType,
          })
        }
        showSuccess('Success', 'Ticket updated successfully')
      } else {
        const created = await apiClient.post(basePath, payload)
        const createdTicketId = created?.data?.data?.id ?? created?.data?.id
        if (!requestorMode && createdTicketId && parentTicketId) {
          await apiClient.post(`${basePath}/${createdTicketId}/relationships`, {
            target_ticket_id: parentTicketId,
            relationship_type: relationshipType,
          })
        }
        showSuccess('Success', 'Ticket created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  const serviceTypeOptions = serviceTypeParents.map((st) => ({
    value: st.id,
    label: st.name,
  }))
  const categorySelectOptions = categoryOptions.map((st) => ({
    value: st.id,
    label: st.name,
  }))
  const subCategorySelectOptions = subCategoryOptions.map((st) => ({
    value: st.id,
    label: st.name,
  }))
  const itemSelectOptions = itemOptions.map((st) => ({
    value: st.id,
    label: st.name,
  }))

  const requesterSelectOptions: RequesterSelectOption[] = useMemo(() => {
    const toOption = (u: RequesterSearchItem): RequesterSelectOption => ({
      value: u.id,
      label: [userLabel(u), u.user_email].filter(Boolean).join(' · ') || u.user_login,
      user: u,
    })

    const options = requesterSearchResults.map(toOption)
    const query = requesterSearchQuery.trim()
    if (query.length < 2 || !currentUser?.id) {
      return options
    }

    const me: RequesterSearchItem = {
      id: currentUser.id,
      user_login: currentUser.user_login,
      user_email: getAuthUserEmail(currentUser),
      first_name: (currentUser as { first_name?: string | null }).first_name ?? null,
      last_name: (currentUser as { last_name?: string | null }).last_name ?? null,
      department_name: currentUserDepartment || null,
    }

    const q = query.toLowerCase()
    const matchesMe = [me.user_login, me.user_email, userLabelFromRequester(me), userLabel(me)]
      .filter(Boolean)
      .some((part) => String(part).toLowerCase().includes(q))

    if (matchesMe && !options.some((o) => o.value === me.id)) {
      return [toOption(me), ...options]
    }

    return options
  }, [requesterSearchResults, requesterSearchQuery, currentUser, currentUserDepartment])

  const selectedRequesterOption: RequesterSelectOption | null = selectedRequester
    ? {
        value: selectedRequester.id,
        label:
          [userLabel(selectedRequester), selectedRequester.user_email].filter(Boolean).join(' · ') ||
          selectedRequester.user_login,
        user: selectedRequester,
      }
    : null

  const requesterSelectField = showRequesterSelect ? (
    <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
      <FormLabel pt={2} fontWeight="medium">Requester <Text as="span" color="red.500">*</Text></FormLabel>
      <FormControl isInvalid={!!errors.requester?.length}>
        <ReactSelect<RequesterSelectOption, false>
          instanceId="ticket-requester-select"
          options={requesterSelectOptions}
          value={selectedRequesterOption}
          onChange={(opt) => {
            if (opt) selectRequesterUser(opt.user)
            else clearRequesterUser()
          }}
          inputValue={requesterSearchQuery}
          onInputChange={(value, meta) => {
            if (meta.action === 'input-change') setRequesterSearchQuery(value)
          }}
          filterOption={() => true}
          isSearchable
          isClearable
          isLoading={requesterSearching}
          placeholder="Search requester (type at least 2 characters)..."
          noOptionsMessage={({ inputValue }) =>
            (inputValue?.trim().length ?? 0) < 2
              ? 'Type at least 2 characters to search'
              : 'No users found'
          }
          loadingMessage={() => 'Searching…'}
        />
        <FormErrorMessage>{errors.requester?.[0]}</FormErrorMessage>
        <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
          {isStaffRequesterAutofillCreate
            ? 'Search and select a requester to auto-fill contact details for CSAT routing.'
            : 'Optionally link this ticket to an existing user account (so they can see it in My Requests).'}
        </FormHelperText>
      </FormControl>
    </Grid>
  ) : null

  if (loading) return <Box py={8}>Loading...</Box>

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {ticketRequestId && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Request #</FormLabel>
            <FormControl>
              <Input value={form.request_number} isReadOnly bg="gray.50" />
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Service type <Text as="span" color="red.500">*</Text></FormLabel>
          <FormControl isInvalid={!!errors.service_type_id?.length}>
            <ReactSelect
              options={serviceTypeOptions}
              value={serviceTypeOptions.find(opt => opt.value === form.service_type_id) || null}
              onChange={(selected) => {
                updateField('service_type_id', selected ? selected.value : '')
              }}
              placeholder="Select service type..."
              isClearable
            />
            <FormErrorMessage>{errors.service_type_id?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>


        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
          <FormLabel pt={2}>Category</FormLabel>
          <FormControl>
            <ReactSelect
              options={parentCategories.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              value={
                parentCategories
                  .map((c) => ({ value: c.id, label: c.name }))
                  .find((opt) => opt.value === selectedParent) || null
              }
              onChange={(selected) => {
                const nextParent = selected ? selected.value : ''
                setSelectedParent(nextParent)
                updateField('category_id', nextParent)
              }}
              placeholder="Search parent category..."
              isClearable
            />
          </FormControl>
        </Grid>

        {selectedParent && subCategories.length > 0 && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
            <FormLabel pt={2}>Sub Category</FormLabel>
            <FormControl>
              <ReactSelect
                options={subCategories.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
                value={
                  subCategories
                    .map((c) => ({ value: c.id, label: c.name }))
                    .find((opt) => opt.value === selectedSubCategory) || null
                }
                onChange={(selected) => {
                  const nextSubCategory = selected ? selected.value : ''
                  setSelectedSubCategory(nextSubCategory)
                  updateField('sub_category_id', nextSubCategory)
                }}
                placeholder="Search subcategory..."
                isClearable
              />
            </FormControl>
          </Grid>
        )}

        {selectedSubCategory && items.length > 0 && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4}>
            <FormLabel pt={2}>Item</FormLabel>

            <FormControl>
              <ReactSelect
                options={items.map((i) => ({
                  value: i.id,
                  label: i.name,
                }))}
                value={
                  items
                    .map((i) => ({ value: i.id, label: i.name }))
                    .find((opt) => opt.value === selectedItem) || null
                }
                onChange={(selected) => {
                  const nextItem = selected ? selected.value : ''
                  setSelectedItem(nextItem)
                  updateField('item_id', nextItem)
                }}
                placeholder="Search item..."
                isClearable
              />
            </FormControl>
          </Grid>
        )}



        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Severity <Text as="span" color="red.500">*</Text></FormLabel>
            <FormControl isInvalid={!!errors.slas_id?.length}>
              <Select
                value={form.slas_id === '' ? '' : form.slas_id}
                onChange={(e) => updateField('slas_id', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Select severity"
              >
                {slas.map((s) => (
                  <option key={s.id} value={s.id}>Severity {s.severity ?? s.id}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors.slas_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Priority <Text as="span" color="red.500">*</Text></FormLabel>
            <FormControl isInvalid={!!errors.ticket_priority_id?.length}>
              <Select
                value={form.ticket_priority_id === '' ? '' : form.ticket_priority_id}
                onChange={(e) => updateField('ticket_priority_id', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="Select priority"
              >
                {ticketPriorities.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label ?? `Priority ${s.level ?? s.id}`}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.ticket_priority_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description <Text as="span" color="red.500">*</Text></FormLabel>
          <FormControl isInvalid={!!errors.description?.length}>
            <Textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} placeholder="Request description" rows={3} />
            <FormErrorMessage>{errors.description?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Location {!ticketRequestId && <Text as="span" color="red.500">*</Text>}
          </FormLabel>
          <FormControl isInvalid={!!errors.location?.length}>
            <Input
              value={form.location}
              onChange={(e) => updateField('location', e.target.value)}
              placeholder="e.g. Building, floor, site, or city"
            />
            <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
              Where the issue applies (e.g. building, floor, site, or area).
              {!ticketRequestId && ' Required for new tickets.'}
            </FormHelperText>
            <FormErrorMessage>{errors.location?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Attachment</FormLabel>
          <FormControl>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={onFilesSelected}
              display="none"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Add files
            </Button>
            {attachments.length > 0 && (
              <SimpleGrid columns={{ base: 2, sm: 4, md: 5 }} spacing={3} mt={3}>
                {attachments.map((item, index) => {
                  const imageUrl = item.preview?.startsWith('blob:') ? item.preview : (item.isExisting && item.savedMeta?.file_url && item.savedMeta?.type?.startsWith('image/') ? item.savedMeta.file_url : null)
                  return (
                  <Box key={index} position="relative" boxShadow="md" borderRadius="md" overflow="hidden" bg="gray.100">
                    {imageUrl ? (
                      <Box
                        as="button"
                        type="button"
                        onClick={() => openZoom(item)}
                        cursor="pointer"
                        display="block"
                        w="full"
                        h="24"
                        position="relative"
                      >
                        <Image src={imageUrl} alt={item.savedMeta?.name ?? item.file.name} objectFit="cover" w="full" h="full" />
                        <Box position="absolute" inset={0} bg="blackAlpha.400" opacity={0} _hover={{ opacity: 1 }} transition="opacity 0.2s" display="flex" alignItems="center" justifyContent="center">
                          <ZoomIn color="white" size={24} />
                        </Box>
                      </Box>
                    ) : (
                      <Box w="full" h="24" display="flex" alignItems="center" justifyContent="center" bg="gray.200">
                        <FileIcon size={28} />
                      </Box>
                    )}
                    <Text noOfLines={2} fontSize="xs" px={2} py={1} title={item.savedMeta?.name ?? item.file.name}>{item.savedMeta?.name ?? item.file.name}</Text>
                    <IconButton
                      aria-label="Remove"
                      icon={<X size={14} />}
                      size="xs"
                      position="absolute"
                      top={1}
                      right={1}
                      colorScheme="red"
                      onClick={() => removeAttachment(index)}
                    />
                  </Box>
                  )
                })}
              </SimpleGrid>
            )}
          </FormControl>
        </Grid>

        {isRequesterAfterAttachment && requesterSelectField}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact name</FormLabel>
          <FormControl isInvalid={!!errors.contact_name?.length}>
            <Input
              value={form.contact_name}
              onChange={(e) => updateField('contact_name', e.target.value)}
              placeholder="Contact name"
              isReadOnly={isContactFieldsReadOnly}
              isDisabled={isContactFieldsReadOnly}
              bg={isContactFieldsReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.contact_name?.[0]}</FormErrorMessage>
            {isRequestorCreateAutofill && (
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Auto-populated from your account.
              </FormHelperText>
            )}
            {isStaffRequesterAutofill && (
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Auto-populated from selected requester.
              </FormHelperText>
            )}
            {isStaffRequesterAutofillCreate && !selectedRequester && (
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Select a requester to auto-fill contact details.
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact email</FormLabel>
          <FormControl isInvalid={!!errors.contact_email?.length}>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => updateField('contact_email', e.target.value)}
              placeholder="Contact email"
              isReadOnly={isContactFieldsReadOnly}
              isDisabled={isContactFieldsReadOnly}
              bg={isContactFieldsReadOnly ? 'gray.50' : undefined}
            />
            <FormErrorMessage>{errors.contact_email?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact number</FormLabel>
          <FormControl isInvalid={!!errors.contact_number?.length}>
            <Input value={form.contact_number} onChange={(e) => updateField('contact_number', e.target.value)} placeholder="Contact number" />
            <FormErrorMessage>{errors.contact_number?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        {isRequestorCreateAutofill && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Requester <Text as="span" color="red.500">*</Text></FormLabel>
            <FormControl>
              <Input
                value={
                  [userLabel(currentUser), currentUserContactEmail].filter(Boolean).join(' · ') ||
                  currentUser?.user_login ||
                  ''
                }
                isReadOnly
                bg="gray.50"
              />
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Auto-populated from your account.
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        {!isRequesterAfterAttachment && requesterSelectField}

        {!ticketRequestId && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Division</FormLabel>
            <FormControl>
              <Input
                value={requesterDepartment}
                isReadOnly
                placeholder={
                  requestorMode || isRequestorCreateAutofill
                    ? 'Division auto-loaded from your profile'
                    : 'Division auto-loaded from selected requester'
                }
                bg="gray.50"
              />
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Auto-populated from user details.
              </FormHelperText>
            </FormControl>
          </Grid>
        )}

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Related to</FormLabel>
            <FormControl isInvalid={!!errors.parent_ticket_id?.length}>
              <Select
                mb={2}
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
              >
                {RELATIONSHIP_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
              <Box position="relative">
                {relatedToDisplay ? (
                  <HStack>
                    <Input value={relatedToDisplay} isReadOnly bg="gray.50" />
                    <Button type="button" size="sm" variant="outline" onClick={clearRelatedTicket}>
                      Clear
                    </Button>
                  </HStack>
                ) : (
                  <>
                    <Input
                      value={relatedSearchQuery}
                      onChange={(e) => setRelatedSearchQuery(e.target.value)}
                      placeholder="Search by request number..."
                      autoComplete="off"
                    />
                    {relatedSearching && (
                      <Text fontSize="sm" color="gray.500" mt={1}>Searching...</Text>
                    )}
                    {relatedSearchResults.length > 0 && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        shadow="lg"
                        zIndex={10}
                        maxH="200px"
                        overflowY="auto"
                      >
                        {relatedSearchResults.map((t) => (
                          <Box
                            key={t.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => selectRelatedTicket(t)}
                          >
                            <Text fontWeight="medium">{t.request_number}</Text>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <FormErrorMessage>{errors.parent_ticket_id?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        {!requestorMode && (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Assign to</FormLabel>
            <FormControl isInvalid={!!errors.assigned_to?.length}>
              <Box position="relative">
                {assignToDisplay ? (
                  <HStack>
                    <Input value={assignToDisplay} isReadOnly bg="gray.50" />
                    <Button type="button" size="sm" variant="outline" onClick={clearAssignTo}>
                      Clear
                    </Button>
                  </HStack>
                ) : (
                  <>
                    <Input
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      onFocus={() => {
                        if (userSearchQuery.trim().length >= 2 && userSearchResults.length === 0) {
                          searchUsers(userSearchQuery)
                        }
                      }}
                      placeholder="Search by login, email, first or last name (min 2 characters)..."
                      autoComplete="off"
                    />
                    {userSearching && (
                      <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                        <Spinner size="sm" />
                      </Box>
                    )}
                    {userSearchResults.length > 0 && !userSearching && (
                      <Box
                        position="absolute"
                        top="100%"
                        left={0}
                        right={0}
                        mt={1}
                        bg="white"
                        borderWidth="1px"
                        borderRadius="md"
                        shadow="lg"
                        zIndex={10}
                        maxH="240px"
                        overflowY="auto"
                      >
                        {userSearchResults.map((user) => (
                          <Box
                            key={user.id}
                            px={3}
                            py={2}
                            cursor="pointer"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => selectAssignUser(user)}
                            borderBottomWidth="1px"
                            borderColor="gray.100"
                            _last={{ borderBottom: 'none' }}
                          >
                            <Text fontWeight="medium" fontSize="sm">
                              {userLabel(user)}
                            </Text>
                            {user.user_email && (
                              <Text fontSize="xs" color="gray.500" mt={0.5}>
                                {user.user_email}
                              </Text>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
              <FormHelperText fontSize="xs" color="gray.500" mt={1} mb={0}>
                Assign ticket to a user (search by login, email, first or last name).
              </FormHelperText>
              {currentUser && (
                <Checkbox
                  mt={3}
                  isChecked={assignToMe}
                  onChange={handleAssignToMeChange}
                  colorScheme="blue"
                  size="sm"
                >
                  <Text as="span" fontSize="sm">Assign to me</Text>
                </Checkbox>
              )}
              <FormErrorMessage>{errors.assigned_to?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>
        )}

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && <Button variant="outline" onClick={onCancel} isDisabled={saving}>Cancel</Button>}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={ticketRequestId ? 'Saving...' : 'Creating...'}>
            {ticketRequestId ? 'Save' : 'Create Ticket'}
          </Button>
        </HStack>
      </VStack>

      <Modal isOpen={isZoomOpen} onClose={closeZoom} size="full">
        <ModalOverlay />
        <ModalContent bg="blackAlpha.900">
          <ModalCloseButton color="white" zIndex={2} />
          <ModalBody display="flex" alignItems="center" justifyContent="center" py={12}>
            {(zoomAttachment?.preview?.startsWith('blob:') || (zoomAttachment?.isExisting && zoomAttachment?.savedMeta?.file_url)) ? (
              <Image
                src={zoomAttachment.preview?.startsWith('blob:') ? zoomAttachment.preview : zoomAttachment?.savedMeta?.file_url ?? ''}
                alt={zoomAttachment?.savedMeta?.name ?? zoomAttachment?.file.name}
                maxH="90vh"
                maxW="100%"
                objectFit="contain"
                onClick={closeZoom}
                cursor="pointer"
              />
            ) : (
              <Text color="white">Preview not available for this file: {zoomAttachment?.savedMeta?.name ?? zoomAttachment?.file.name}</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}
